import React, { useState, useContext, useEffect, useRef } from 'react'
import ResponsiveAppBar from '../../components/NavbarCustomer'
import { getList } from '../../services/product'
import { getItemList as geteOrderItems } from '../../services/order'
import { UserContext } from '../../context/UserProvider.jsx'
import { DataGrid } from '@mui/x-data-grid'
import { Box, Button, Container, MenuItem, TextField, Typography, Chip, Alert } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import ReplayIcon from '@mui/icons-material/Replay'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SearchIcon from '@mui/icons-material/Search'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import './orderInfo.css'

const App = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { token } = useContext(UserContext)
  const [rows, setRows] = useState([])
  const [pageInfoCurrentPage, setPageInfoCurrentPage] = useState(0)
  const [pageInfoTotalRows, setPageInfoTotalRows] = useState(0)
  const [pageInfoRowPerPage, setPageInfoRowPerPage] = useState(25)
  const [loading, setLoading] = useState(true)
  const [filterField, setFilterField] = useState('name')
  const searchRef = useRef(null)
  const [filters, setFilters] = useState({})
  const [error, setError] = useState('')
  const [retry, setRetry] = useState(0)
  const columns = [
    {
      field: 'name', headerName: 'Product', flex: 1, minWidth: 300,
      renderCell: ({ row }) => <div className="order-info-product">
        <div className="order-info-image">{row.image ? <img src={row.image} alt="" /> : <Inventory2OutlinedIcon aria-hidden="true" />}</div>
        <div><span className="order-info-item-number">{row['item-number']}</span><strong>{row.name}</strong></div>
      </div>,
    },
    { field: 'category', headerName: 'Category', width: 240 },
    { field: 'qty', headerName: 'Quantity', width: 110, align: 'center', headerAlign: 'center',
      renderCell: ({ value }) => <strong className="order-info-quantity">{value}</strong> },
    { field: 'type', headerName: 'Order by', width: 110,
      renderCell: ({ value }) => <Chip size="small" label={value} sx={{ borderRadius: '6px', fontWeight: 600, fontSize: 11, bgcolor: '#edf4fb', color: '#245780' }} /> },
  ]

  useEffect(() => {
    let active = true
    const requireCollection = (response) => {
      if (!Array.isArray(response?.collection)) {
        throw new Error(response?.data?.message || response?.message || 'Unable to load order items. Please try again.')
      }
      return response.collection
    }
    async function load() {
      setLoading(true)
      setError('')
      try {
        const orderResponse = await geteOrderItems(token, {
          'current-page': 1, 'record-per-page': 'all', 'user-specific': false, fields: { 'order-id': id },
        })
        if (!active) return
        const items = requireCollection(orderResponse)
        const byProduct = new Map(items.map(item => [Number(item.data['product-id']), item.data]))
        if (!byProduct.size) {
          setRows([])
          setPageInfoTotalRows(0)
          return
        }
        const response = await getList(token, {
          'current-page': pageInfoCurrentPage + 1,
          'record-per-page': pageInfoRowPerPage,
          entities: [...byProduct.keys()], 'user-specific': false, fields: filters,
        })
        if (!active) return
        const products = requireCollection(response)
        setRows(products.map(item => ({
          ...item.data,
          id: item.raw.id,
          qty: byProduct.get(Number(item.raw.id))?.qty ?? 0,
          type: String(byProduct.get(Number(item.raw.id))?.type || '').toUpperCase(),
        })))
        setPageInfoTotalRows(Number(response['data-total']) || 0)
      } catch (caught) {
        if (active) setError(caught.message || 'Unable to load order items. Please try again.')
      } finally {
        if (active) setLoading(false)
      }
    }
    void load()
    return () => { active = false }
  }, [id, token, pageInfoCurrentPage, pageInfoRowPerPage, filters, retry])

  const updatePageHandler = ({ page, pageSize }) => {
    setPageInfoCurrentPage(pageSize !== pageInfoRowPerPage ? 0 : page)
    setPageInfoRowPerPage(pageSize)
  }

  const searchHandler = () => {
    setPageInfoCurrentPage(0)
    setFilters({ [filterField]: searchRef.current.value.trim() })
  }

  return (
    <>
      <ResponsiveAppBar />
      <Box className="order-info-page">
        <Container maxWidth="xl">
          <Button className="order-info-back" startIcon={<ArrowBackIcon />} onClick={() => navigate('/orders')}>Order history</Button>
          <div className="order-info-heading">
            <div>
              <Typography component="h1" variant="h4" sx={{ fontWeight: 700, letterSpacing: '-.7px' }}>Order Information</Typography>
              <Typography sx={{ color: 'text.secondary', mt: 1 }}>Review the items in this order or add them to a new cart.</Typography>
            </div>
            <Button variant="contained" disableElevation startIcon={<ReplayIcon />} className="order-info-reorder" onClick={() => navigate('/products', { state: { reorderOrderId: id } })}>Reorder</Button>
          </div>
          <section className="order-info-panel" aria-label="Order items">
            <div className="order-info-panel-heading">
              <div className="order-info-section-title"><Inventory2OutlinedIcon aria-hidden="true" /><Typography component="h2" sx={{ fontSize: 18, fontWeight: 700 }}>Order items</Typography><Chip size="small" label={loading ? 'Loading…' : pageInfoTotalRows} /></div>
              <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>Original quantities and pack types</Typography>
            </div>
            <Box component="form" className="order-info-search" onSubmit={event => { event.preventDefault(); searchHandler() }}>
              <TextField select label="Search by" value={filterField} onChange={event => setFilterField(event.target.value)} size="small">
                <MenuItem value="name">Product name</MenuItem><MenuItem value="item-number">Item number</MenuItem><MenuItem value="category">Category</MenuItem>
              </TextField>
              <TextField label="Search this order" placeholder="Enter a name, item number or category" inputRef={searchRef} size="small" />
              <Button type="submit" variant="contained" disableElevation startIcon={<SearchIcon />}>Search</Button>
              <Button type="button" variant="text" onClick={() => { searchRef.current.value = ''; setPageInfoCurrentPage(0); setFilters({}) }}>Clear</Button>
            </Box>
            {error && <Alert severity="error" sx={{ mx: 3, mb: 2 }} action={<Button color="inherit" disabled={loading} onClick={() => setRetry(value => value + 1)}>Retry</Button>}>{error}</Alert>}
            <div className="order-info-table">
              <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                paginationModel={{ pageSize: pageInfoRowPerPage, page: pageInfoCurrentPage }}
                pageSizeOptions={[10, 25, 50, 100]}
                pagination
                paginationMode="server"
                rowCount={pageInfoTotalRows}
                loading={loading}
                onPaginationModelChange={updatePageHandler}
                disableColumnFilter
                disableColumnMenu
                disableRowSelectionOnClick
                rowHeight={96}
                sx={{ border: 0, minWidth: 760, '& .MuiDataGrid-columnHeaders': { bgcolor: '#f7f9fc', color: '#526176' }, '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 }, '& .MuiDataGrid-cell': { borderColor: '#edf1f5', fontSize: 14 }, '& .MuiDataGrid-footerContainer': { borderColor: '#edf1f5' }, '& .MuiDataGrid-row:hover': { bgcolor: '#f8fbff' }, '& .MuiDataGrid-overlayWrapper': { minHeight: 160 } }}
              />
            </div>
          </section>
        </Container>
      </Box>
    </>
  )
}

export default App
